import React from 'react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';

const SemanticDatabaseCard = ({
  record,
  connectionName,
  connectionType,
  tableCount = 0,
  onOpenTables,
  onView,
  onEdit,
  onDelete,
  isDeleted
}) => {
  const usersCount = Array.isArray(record?.users_allowed) ? record.users_allowed.length : 0;
  const deletedAt = record?.soft_delete?.deleted_at;
  const statusLabel = isDeleted ? 'Eliminada' : record?.is_active === false ? 'Inactiva' : 'Activa';
  const statusColor = isDeleted ? 'var(--wb-danger)' : 'var(--wb-success-strong)';

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'var(--wb-bg-card)',
        borderRadius: 2,
        p: 2,
        mb: 2,
        border: '1px solid var(--wb-overlay-10)'
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              bgcolor: 'var(--wb-x-bg-38bdf81f, rgba(56, 189, 248, 0.12))',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <StorageIcon sx={{ color: 'var(--wb-accent)' }} />
          </Box>
        </Grid>
        <Grid item xs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="subtitle1"
              color="var(--wb-accent)"
              onClick={() => !isDeleted && onOpenTables?.(record)}
              sx={{
                fontWeight: 600,
                cursor: isDeleted ? 'default' : 'pointer',
                textDecoration: isDeleted ? 'none' : 'underline',
                textDecorationColor: 'var(--wb-accent-40)',
                '&:hover': isDeleted ? {} : { color: 'var(--wb-x-text-7dd3fc, #7dd3fc)' }
              }}
            >
              {record?.name || 'Sin nombre'}
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: 'var(--wb-overlay-08)',
                color: statusColor,
                borderRadius: 1
              }}
            />
            {record?.version_id && (
              <Chip
                label={`Versión ${record.version_id}`}
                size="small"
                sx={{ backgroundColor: 'var(--wb-x-bg-94a3b82e, rgba(148, 163, 184, 0.18))', color: 'var(--wb-text-label)' }}
              />
            )}
          </Box>

          <Typography variant="body2" color="var(--wb-text-soft)" sx={{ mt: 0.5 }}>
            {record?.description || 'Sin descripci?n registrada.'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StorageIcon sx={{ color: 'var(--wb-muted)', fontSize: 18 }} />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {connectionName || 'Conexión no encontrada'}
                {connectionType ? ` ? ${connectionType}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PeopleIcon sx={{ color: 'var(--wb-muted)', fontSize: 18 }} />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {usersCount === 1 ? '1 usuario con acceso' : `${usersCount} usuarios con acceso`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: isDeleted ? 'default' : 'pointer'
              }}
              onClick={() => !isDeleted && onOpenTables?.(record)}
            >
              <TableChartOutlinedIcon sx={{ color: 'var(--wb-muted)', fontSize: 18 }} />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {tableCount === 1 ? '1 tabla' : `${tableCount} tablas`}
              </Typography>
            </Box>
          </Box>

          {isDeleted && deletedAt && (
            <Typography variant="caption" color="var(--wb-x-text-f87171d9, rgba(248, 113, 113, 0.85))" sx={{ display: 'block', mt: 1 }}>
              Eliminada el {new Date(deletedAt).toLocaleString()}
            </Typography>
          )}
        </Grid>
        <Grid item>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Ver detalles">
              <IconButton onClick={() => onView(record)} sx={{ color: 'var(--wb-text-strong)' }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onEdit(record)}
                  sx={{ color: 'var(--wb-text-strong)' }}
                  disabled={isDeleted}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Eliminar" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onDelete(record)}
                  sx={{ color: isDeleted ? 'var(--wb-text-ghost)' : 'var(--wb-danger)' }}
                  disabled={isDeleted}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SemanticDatabaseCard;